import { useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import './Receivers.css';
import AddReceiver from './AddReceiver';
import ReceiverDetail from './ReceiverDetail';
import { getReceiversMetadata, getAllReceivers } from '../utils/httpApi';
import { isApiSuccess } from '../utils/api';
import { formatRelativeTime } from '../utils/dateTime';
import ReceiverOverviewMap from './ReceiverOverviewMap';
import {
  isAviotrackActive,
  hasEverSentStats,
  softwareLabel,
  deviceTypeLabel,
  trafficTypes,
  locationLabel,
  coordinatesLabel,
  updateState,
} from '../utils/receiverSoftware';
import { getReceiversViewState, updateReceiversViewState } from './receiversViewState';

// Phone breakpoint for the list view. Below this width the wide table is
// unusable — render stacked cards instead.
const PHONE_MAX_PX = 600;

// How long the cached all-receivers list is trusted on a remount before a
// fresh fetch replaces it (the poll refreshes it anyway once mounted).
const ALL_RECEIVERS_POLL_MS = 60_000;

function useIsPhone() {
  const [isPhone, setIsPhone] = useState(
    typeof window !== 'undefined' && window.innerWidth <= PHONE_MAX_PX
  );
  useEffect(() => {
    const onResize = () => setIsPhone(window.innerWidth <= PHONE_MAX_PX);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isPhone;
}

// Backend three-state status → 'online' | 'connected' | 'offline'.
function getStatus(receiver) {
  if (receiver.status) {
    switch (receiver.status) {
      case 'ACTIVE': return 'online';
      case 'CONNECTED': return 'connected';
      case 'OFFLINE': return 'offline';
      default: break;
    }
  }
  // getAllReceivers rows carry connectionStatus; the persisted isActive is the last resort.
  if (receiver.connectionStatus) {
    return receiver.connectionStatus === 'Active' ? 'online'
      : receiver.connectionStatus === 'Connected' ? 'connected' : 'offline';
  }
  return receiver.isActive ? 'online' : 'offline';
}

function StatusBadge({ status }) {
  return (
    <span className={`status-badge-inline ${status}`}>
      {status === 'online' ? 'Online' : status === 'connected' ? 'Connected' : 'Offline'}
    </span>
  );
}

function SoftwareCell({ meta, receiver }) {
  const sw = softwareLabel(meta, receiver);
  if (!sw) return <span className="muted">—</span>;
  if (sw.declared) {
    return <span className="software-badge declared" title="Software declared on the receiver's setup page">{sw.label}</span>;
  }
  return <span className={`software-badge ${isAviotrackActive(meta) ? 'fresh' : 'stale'}`}>{sw.label}</span>;
}

function TrafficBadges({ types }) {
  if (!types.length) return <span className="protocol-badge-none">—</span>;
  const cls = (t) => t.toLowerCase().replace(/[^a-z0-9]/g, '');
  return (
    <div className="protocol-badges">
      {types.map(t => (
        <span
          key={t.type}
          className={`protocol-badge ${cls(t.type)} ${t.fresh ? 'active' : ''}`}
          title={t.lastSeenAt ? `Last seen ${formatRelativeTime(t.lastSeenAt)}` : 'Receiving now'}
        >{t.type}</span>
      ))}
    </div>
  );
}

function RateCell({ receiver }) {
  if (receiver.beastMsgRate == null && receiver.acarsMsgRate == null) {
    return <span className="rate-badge idle">—</span>;
  }
  const idle = !receiver.beastMsgRate && !receiver.acarsMsgRate && !receiver.otherMsgRate;
  return (
    <div className="socket-rates">
      {receiver.beastMsgRate > 0 && (
        <span className="rate-badge beast" title="Beast ADS-B messages/sec">B: {receiver.beastMsgRate.toFixed(1)}</span>
      )}
      {receiver.acarsMsgRate > 0 && (
        <span className="rate-badge acars" title="ACARS/VDL2 messages/sec">A: {receiver.acarsMsgRate.toFixed(1)}</span>
      )}
      {receiver.otherMsgRate > 0 && (
        <span className="rate-badge other" title="Other socket messages/sec">O: {receiver.otherMsgRate.toFixed(1)}</span>
      )}
      {idle && <span className="rate-badge idle">idle</span>}
    </div>
  );
}

// Backlog is how far behind the receiver's Beast stream is running. Sub-second
// is healthy; seconds mean the server is falling behind on that stream.
function BacklogCell({ ms }) {
  if (ms === null || ms === undefined) return <span className="muted">—</span>;
  const secs = ms / 1000;
  const cls = secs >= 10 ? 'backlog-bad' : secs >= 2 ? 'backlog-warn' : 'backlog-ok';
  const label = ms < 1000 ? `${ms} ms` : `${secs.toFixed(1)} s`;
  return <span className={`backlog-pill ${cls}`} title={`${ms} ms behind`}>{label}</span>;
}

function UpdateCell({ meta, onOpenDevice }) {
  const u = updateState(meta);
  if (u.state === 'unknown') return <span className="muted">—</span>;
  if (u.state === 'current') return <span className="update-current" title={`Latest: v${u.latestVersion || '?'}`}>Up to date</span>;
  const label = u.state === 'installer' ? `⬆ v${u.latestVersion} · installer` : `⬆ v${u.latestVersion}`;
  const title = u.state === 'installer'
    ? 'Newer build needs a newer installer — open the Device tab to download it'
    : 'Update available — open the Device tab to push it';
  return (
    <button className="update-badge" title={title} onClick={(e) => { e.stopPropagation(); onOpenDevice(); }}>
      {label}
    </button>
  );
}

function fmtCount(n) {
  return n === null || n === undefined ? null : Number(n).toLocaleString();
}

// Live websocket stats first (per second fresh), else the daily row from
// getReceiversMetadata (populated for every feeder type since backend 1.0.429).
function todayCounts(stats, meta) {
  const aircraft = stats?.totalUniqueAircraftToday ?? meta?.aircraftToday ?? null;
  const msgs = stats?.totalMessagesToday ?? meta?.messagesToday ?? null;
  return { aircraft, msgs };
}

function sortValue(receiver, meta, stats, key) {
  const m = meta[receiver.receiverUuid];
  const s = stats;
  switch (key) {
    case 'receiverName': return (receiver.receiverName || '').toLowerCase();
    case 'emailAddress': return (receiver.emailAddress || '').toLowerCase();
    case 'status': { const st = getStatus(receiver); return st === 'online' ? 0 : st === 'connected' ? 1 : 2; }
    case 'software': return (softwareLabel(m, receiver)?.label || '').toLowerCase();
    case 'deviceType': return (deviceTypeLabel(m, receiver) || '').toLowerCase();
    case 'version': return (m?.softwareVersion || '').toLowerCase();
    case 'traffic': return trafficTypes(receiver, s).map(t => t.type).join(',').toLowerCase();
    case 'rate': return -((receiver.beastMsgRate || 0) + (receiver.acarsMsgRate || 0));
    case 'location': return (locationLabel(receiver) || '').toLowerCase();
    case 'lastActive': return -new Date(receiver.lastActiveAt || receiver.lastConnectedAt || 0).getTime();
    case 'aircraftToday': return todayCounts(s, m).aircraft ?? -1;
    case 'messagesToday': return todayCounts(s, m).msgs ?? -1;
    case 'backlog': return m?.backlogMillis ?? -1;
    case 'update': { const u = updateState(m).state; return u === 'update' ? 0 : u === 'installer' ? 1 : u === 'current' ? 2 : 3; }
    default: return '';
  }
}

function Receivers({
  receivers = [],
  receiverStatistics = null,
  onAddReceiver,
  onUpdateReceiver,
  onDeleteReceiver,
  preselectUuid,
  onPreselectConsumed,
  token,
  isAdmin = false,
  onViewLive,
  onNavigate,
  initialScope,
  onInitialScopeConsumed,
}) {
  const saved = getReceiversViewState();
  const [showAddReceiver, setShowAddReceiver] = useState(false);
  const [selectedReceiver, setSelectedReceiver] = useState(null);
  const [apiErrors, setApiErrors] = useState([]);
  const [savedReceiver, setSavedReceiver] = useState(null);
  const isPhone = useIsPhone();

  // ---- view state (persisted across unmounts in receiversViewState) ----
  const [scope, setScope] = useState(() => (isAdmin && (initialScope || saved.scope) === 'all') ? 'all' : 'mine');
  const [statusFilter, setStatusFilter] = useState(saved.statusFilter);
  const [ownerFilter, setOwnerFilter] = useState(saved.ownerFilter);
  const [groupByOwner, setGroupByOwner] = useState(saved.groupByOwner);
  const [sort, setSort] = useState(saved.sort);
  useEffect(() => {
    updateReceiversViewState({ scope, statusFilter, ownerFilter, groupByOwner, sort });
  }, [scope, statusFilter, ownerFilter, groupByOwner, sort]);
  // An explicit request (Admin → "All Receivers →") wins over the remembered
  // scope, once; after that the user's own toggling is what gets remembered.
  useEffect(() => {
    if (!initialScope) return;
    if (isAdmin) setScope(initialScope === 'all' ? 'all' : 'mine');
    if (onInitialScopeConsumed) onInitialScopeConsumed();
  }, [isAdmin, initialScope, onInitialScopeConsumed]);

  // ---- all-receivers rows (admin scope) ----
  const [allReceivers, setAllReceivers] = useState(saved.allReceivers || []);
  const [allError, setAllError] = useState(null);
  useEffect(() => {
    if (!isAdmin || scope !== 'all' || !token) return undefined;
    let alive = true;
    const fetchAll = async () => {
      try {
        const resp = await getAllReceivers(token);
        if (!alive) return;
        if (isApiSuccess(resp)) {
          const rows = resp.receivers || [];
          setAllReceivers(rows);
          setAllError(null);
          updateReceiversViewState({ allReceivers: rows, allLoadedAt: Date.now() });
        } else {
          setAllError('Could not load all receivers');
        }
      } catch (e) {
        if (alive) setAllError('Could not load all receivers');
      }
    };
    // Cached rows render immediately; only skip the first fetch if they are fresh.
    if (!saved.allReceivers || Date.now() - saved.allLoadedAt > ALL_RECEIVERS_POLL_MS) fetchAll();
    const id = setInterval(fetchAll, ALL_RECEIVERS_POLL_MS);
    return () => { alive = false; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, scope, token]);

  // Live websocket stats by UUID. Admin sessions receive the whole fleet.
  const statsByUuid = useMemo(() => {
    const out = {};
    (receiverStatistics?.receivers || []).forEach(r => { if (r?.receiverUuid) out[r.receiverUuid] = r; });
    return out;
  }, [receiverStatistics]);
  const getReceiverStats = useCallback((uuid) => statsByUuid[uuid] || null, [statsByUuid]);

  // Rows for the current scope. The user's own rows already carry live
  // status/rates (App merges them); all-scope rows get the same treatment here.
  const rows = useMemo(() => {
    if (scope !== 'all') return receivers;
    return allReceivers.map(r => {
      const s = statsByUuid[r.receiverUuid];
      if (!s) return r;
      return {
        ...r,
        isActive: s.isActive,
        status: s.status || (s.isActive ? 'ACTIVE' : 'OFFLINE'),
        lastActiveAt: s.lastActiveAt || r.lastActiveAt,
        beastMsgRate: s.beastMsgRate,
        acarsMsgRate: s.acarsMsgRate,
        otherMsgRate: s.otherMsgRate,
      };
    });
  }, [scope, receivers, allReceivers, statsByUuid]);

  // ---- STATS-derived metadata per receiver UUID (Software / Type / Version,
  // daily totals, backlog, release check). Polled every 60 s. ----
  const [meta, setMeta] = useState({});
  // The backend omits receivers that never sent STATS from the map, so "no entry"
  // only means "not Aviotrack" once a fetch has actually completed.
  const [metaLoaded, setMetaLoaded] = useState(false);
  const [latestReceiverVersion, setLatestReceiverVersion] = useState(null);
  const metaUuidKey = rows.map(r => r.receiverUuid).filter(Boolean).sort().join(',');
  useEffect(() => {
    let alive = true;
    if (!token || !metaUuidKey) {
      setMeta({});
      return () => { alive = false; };
    }
    const uuids = metaUuidKey.split(',');
    const fetchMeta = async () => {
      try {
        const resp = await getReceiversMetadata(uuids, token);
        if (!alive) return;
        if (isApiSuccess(resp) && resp.byUuid) {
          setMeta(resp.byUuid);
          setMetaLoaded(true);
          if (resp.latestReceiverVersion) setLatestReceiverVersion(resp.latestReceiverVersion);
        }
      } catch (e) {
        // Meta is decorative — columns just stay '—'.
      }
    };
    fetchMeta();
    const id = setInterval(fetchMeta, 60_000);
    return () => { alive = false; clearInterval(id); };
  }, [metaUuidKey, token]);

  // Keep the selected receiver fresh, and drop it if it disappears.
  useEffect(() => {
    if (!selectedReceiver) return;
    const updated = rows.find(r => r.receiverUuid === selectedReceiver.receiverUuid);
    if (updated) {
      if (updated !== selectedReceiver) {
        setSelectedReceiver(prev => ({ ...updated, _initialTab: prev?._initialTab }));
      }
    } else if (scope === 'mine') {
      setSelectedReceiver(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  // A user with exactly one receiver lands on it directly (as before), but
  // only once per mount so "← Back to List" still works.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenedRef.current || scope !== 'mine' || selectedReceiver || showAddReceiver) return;
    if (receivers.length === 1 && !isAdmin) {
      autoOpenedRef.current = true;
      setSelectedReceiver(receivers[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receivers, scope]);

  // Deep link (e.g. from the live map header): open the receiver with the
  // requested tab, then tell App we consumed it so it does not re-fire.
  useEffect(() => {
    if (!preselectUuid) return;
    const target = rows.find(r => r.receiverUuid === preselectUuid);
    if (target && target._initialTab) {
      setSelectedReceiver(target);
      if (onPreselectConsumed) onPreselectConsumed(preselectUuid);
    }
  }, [preselectUuid, rows, onPreselectConsumed]);

  // ---- filtering / sorting / grouping ----
  const owners = useMemo(() => {
    const counts = {};
    rows.forEach(r => { const k = r.emailAddress || '(unassigned)'; counts[k] = (counts[k] || 0) + 1; });
    return Object.entries(counts).map(([email, count]) => ({ email, count })).sort((a, b) => b.count - a.count);
  }, [rows]);

  const onlineCount = useMemo(() => rows.filter(r => getStatus(r) !== 'offline').length, [rows]);

  const visibleReceivers = useMemo(() => {
    let list = rows;
    if (scope === 'all' && ownerFilter) list = list.filter(r => (r.emailAddress || '(unassigned)') === ownerFilter);
    if (statusFilter === 'online') list = list.filter(r => getStatus(r) !== 'offline');
    if (statusFilter === 'offline') list = list.filter(r => getStatus(r) === 'offline');
    const sorted = [...list].sort((a, b) => {
      const va = sortValue(a, meta, statsByUuid[a.receiverUuid], sort.key);
      const vb = sortValue(b, meta, statsByUuid[b.receiverUuid], sort.key);
      let cmp = (typeof va === 'number' && typeof vb === 'number') ? va - vb : String(va).localeCompare(String(vb));
      if (cmp === 0) cmp = (a.receiverName || '').localeCompare(b.receiverName || '');
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [rows, scope, ownerFilter, statusFilter, sort, meta, statsByUuid]);

  const displayRows = useMemo(() => {
    if (scope !== 'all' || !groupByOwner) return visibleReceivers;
    const byOwner = new Map();
    visibleReceivers.forEach(r => {
      const email = r.emailAddress || '(unassigned)';
      if (!byOwner.has(email)) byOwner.set(email, []);
      byOwner.get(email).push(r);
    });
    const groups = [...byOwner.entries()].map(([email, list]) => {
      let msgs = 0, aircraft = 0;
      list.forEach(r => {
        const c = todayCounts(statsByUuid[r.receiverUuid], meta[r.receiverUuid]);
        msgs += c.msgs || 0;
        aircraft += c.aircraft || 0;
      });
      return { email, list, msgs, aircraft };
    });
    groups.sort((a, b) => b.msgs - a.msgs || a.email.localeCompare(b.email));
    const out = [];
    groups.forEach(g => {
      out.push({ __group: true, email: g.email, count: g.list.length, msgs: g.msgs, aircraft: g.aircraft });
      out.push(...g.list);
    });
    return out;
  }, [scope, groupByOwner, visibleReceivers, meta, statsByUuid]);

  const NUMERIC_SORT_KEYS = ['rate', 'aircraftToday', 'messagesToday', 'backlog', 'lastActive', 'update'];
  const toggleSort = (key) => {
    setSort(prev => {
      if (prev.key === key) return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      // Metrics default to "biggest first"; lastActive/update sort values are already inverted.
      return { key, dir: NUMERIC_SORT_KEYS.includes(key) && !['lastActive', 'update'].includes(key) ? 'desc' : 'asc' };
    });
  };
  const sortMark = (key) => (sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '');

  // ---- scroll memory for the list container ----
  const listRef = useRef(null);
  const showingList = !showAddReceiver && !selectedReceiver;
  useLayoutEffect(() => {
    if (!showingList || !listRef.current) return;
    const top = getReceiversViewState().scrollTop;
    if (top > 0) listRef.current.scrollTop = top;
  }, [showingList, displayRows.length]);
  const rememberScroll = () => {
    if (listRef.current) updateReceiversViewState({ scrollTop: listRef.current.scrollTop });
  };

  // ---- add / detail handlers ----
  const handleAddReceiver = async (data) => {
    setApiErrors([]);
    const result = await onAddReceiver(data);
    if (result.success) {
      setSavedReceiver(result.receiver);
    } else {
      setApiErrors(result.errors || ['An unknown error occurred']);
    }
  };

  const handleCancelAddReceiver = () => {
    setShowAddReceiver(false);
    setApiErrors([]);
    setSavedReceiver(null);
  };

  const openDetail = (receiver, tab) => {
    rememberScroll();
    setSelectedReceiver(tab ? { ...receiver, _initialTab: tab } : receiver);
  };

  const viewLive = (receiver) => {
    rememberScroll();
    if (onViewLive) onViewLive(receiver);
  };

  const formatDate = (dateString) => formatRelativeTime(dateString);

  // Quick links: Performance / Device need the Aviotrack software (STATS);
  // everything else gets a Setup link to the receiver's own settings tab.
  const quickLinks = (receiver, m, className = 'quicklink') => {
    const everSent = hasEverSentStats(m);
    const stop = (e, tab) => { e.stopPropagation(); openDetail(receiver, tab); };
    if (!everSent) {
      return (
        <span className="quicklinks">
          <button className={className} title="Open the receiver's setup page" onClick={(e) => stop(e, 'settings')}>Setup</button>
        </span>
      );
    }
    const live = isAviotrackActive(m);
    return (
      <span className="quicklinks">
        <button
          className={`${className} ${live ? '' : 'stale'}`}
          title={live ? 'Open Performance tab' : 'Receiver is not currently sending STATS — shows its last known values'}
          onClick={(e) => stop(e, 'stats')}
        >Performance</button>
        <button
          className={`${className} ${live ? '' : 'stale'}`}
          title={live ? 'Open Device tab' : 'Receiver is not currently sending STATS — remote config will queue until it reconnects'}
          onClick={(e) => stop(e, 'config')}
        >Device</button>
      </span>
    );
  };

  // ---------------------------------------------------------------- add page
  if (showAddReceiver) {
    return (
      <div className="receivers-page">
        <div className="receivers-container">
          <div className="receivers-header">
            <div>
              <h2 className="receivers-title">
                <button className="btn-back" onClick={handleCancelAddReceiver}>
                  ← Back to {rows.length > 0 ? 'List' : 'Receivers'}
                </button>
                {savedReceiver ? 'Receiver Added' : 'Add New Receiver'}
              </h2>
            </div>
          </div>
          <AddReceiver
            onSave={handleAddReceiver}
            onCancel={handleCancelAddReceiver}
            apiErrors={apiErrors}
            savedReceiver={savedReceiver}
          />
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------- detail page
  if (selectedReceiver) {
    const receiver = selectedReceiver;
    return (
      <div className="receivers-page">
        <div className="receivers-container">
          {/* Detail header. Deliberately NOT the list header: "+ Add Receiver" is a
              list-level action and had no business here — on a phone it was the largest
              thing on a page about one specific receiver. Back and the receiver's name
              sit on one row, with the single action that applies to this receiver. */}
          <div className="receivers-header receiver-detail-header">
            <button
              className="btn-back"
              onClick={() => setSelectedReceiver(null)}
              aria-label="Back to receiver list"
            >
              <span aria-hidden="true">←</span>
              <span className="btn-back-text">Back</span>
            </button>
            <h2 className="receivers-title receiver-detail-title">{receiver.receiverName}</h2>
            {onViewLive && (
              <button className="btn-view" onClick={() => viewLive(receiver)}>View Live</button>
            )}
          </div>

          <ReceiverDetail
            receiver={receiver}
            receiverStatistics={getReceiverStats(receiver.receiverUuid)}
            receiverMeta={metaLoaded ? (meta[receiver.receiverUuid] || null) : undefined}
            latestReceiverVersion={latestReceiverVersion}
            onNavigate={onNavigate}
            onSave={onUpdateReceiver}
            onDelete={onDeleteReceiver}
            token={token}
          />
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------- list page
  const title = scope === 'all' ? 'All Receivers' : 'My Receivers';
  const summary = scope === 'all'
    ? `${rows.length} receiver${rows.length !== 1 ? 's' : ''} across ${owners.length} owner${owners.length !== 1 ? 's' : ''} · ${onlineCount} online`
    : `${rows.length} receiver${rows.length !== 1 ? 's' : ''} registered`;

  return (
    <div className="receivers-page">
      <div className="receivers-container receivers-container--wide">
        <div className="receivers-header">
          <div>
            <h2 className="receivers-title">{title}</h2>
            <div className="receivers-summary">{summary}</div>
          </div>
          <button className="btn-add" onClick={() => setShowAddReceiver(true)}>+ Add Receiver</button>
        </div>

        <div className="receivers-controls">
          {isAdmin && (
            <div className="receivers-scope">
              <button className={`filter-btn ${scope === 'mine' ? 'active' : ''}`} onClick={() => setScope('mine')}>
                My receivers ({receivers.length})
              </button>
              <button className={`filter-btn ${scope === 'all' ? 'active' : ''}`} onClick={() => setScope('all')}>
                All receivers{allReceivers.length ? ` (${allReceivers.length})` : ''}
              </button>
            </div>
          )}
          <div className="receivers-status-filter">
            <button className={`filter-btn ${statusFilter === '' ? 'active' : ''}`} onClick={() => setStatusFilter('')}>All ({rows.length})</button>
            <button className={`filter-btn ${statusFilter === 'online' ? 'active' : ''}`} onClick={() => setStatusFilter('online')}>Online ({onlineCount})</button>
            <button className={`filter-btn ${statusFilter === 'offline' ? 'active' : ''}`} onClick={() => setStatusFilter('offline')}>Offline ({rows.length - onlineCount})</button>
          </div>
          {scope === 'all' && (
            <>
              <select
                className="receivers-owner-filter"
                value={ownerFilter}
                onChange={(e) => setOwnerFilter(e.target.value)}
                title="Filter the map and table by owner"
              >
                <option value="">All owners ({rows.length})</option>
                {owners.map(o => <option key={o.email} value={o.email}>{o.email} ({o.count})</option>)}
              </select>
              <button className={`filter-btn ${groupByOwner ? 'active' : ''}`} onClick={() => setGroupByOwner(v => !v)}>
                {groupByOwner ? 'Grouped by owner' : 'Group by owner'}
              </button>
            </>
          )}
          {allError && scope === 'all' && <span className="receivers-error">{allError}</span>}
        </div>

        {rows.length === 0 ? (
          <div className="no-receivers">
            {scope === 'all' ? (
              <p>No receivers registered yet.</p>
            ) : (
              <>
                <p>You have no receivers registered yet.</p>
                <p className="help-text">Click "Add Receiver" to register a new receiver.</p>
              </>
            )}
          </div>
        ) : (
          <div className="receivers-layout">
            <div className="receivers-map-col">
              <ReceiverOverviewMap
                receivers={visibleReceivers}
                meta={meta}
                liveStats={statsByUuid}
                isActiveFn={(r) => getStatus(r) !== 'offline'}
                emptyHint="Set a location on a receiver to see it here."
                onSelectReceiver={(receiver) => openDetail(receiver)}
                onViewLive={onViewLive ? (receiver) => viewLive(receiver) : null}
                onOpenTab={(receiver, tab) => openDetail(receiver, tab)}
              />
            </div>

            <div className="receivers-list-col" ref={listRef} onScroll={rememberScroll}>
              {isPhone ? (
                <div className="receivers-mobile-list">
                  {displayRows.map(receiver => {
                    if (receiver.__group) {
                      return (
                        <div key={`group-${receiver.email}`} className="receiver-mcard-group">
                          <span className="owner-group-name">{receiver.email}</span>
                          <span className="owner-group-meta">{receiver.count} receiver{receiver.count === 1 ? '' : 's'}</span>
                        </div>
                      );
                    }
                    const status = getStatus(receiver);
                    const stats = getReceiverStats(receiver.receiverUuid);
                    const m = meta[receiver.receiverUuid];
                    const counts = todayCounts(stats, m);
                    const types = trafficTypes(receiver, stats);
                    const sw = softwareLabel(m, receiver);
                    const type = deviceTypeLabel(m, receiver);
                    return (
                      <div
                        key={receiver.receiverUuid}
                        className="receiver-mcard"
                        onClick={() => openDetail(receiver)}
                        role="button"
                        tabIndex={0}
                      >
                        <div className="receiver-mcard-head">
                          <div className="receiver-mcard-title">
                            <span className={`receiver-mcard-dot ${status}`} aria-hidden="true" />
                            <span className="receiver-mcard-name">{receiver.receiverName}</span>
                          </div>
                          <StatusBadge status={status} />
                        </div>

                        <div className="receiver-mcard-meta">
                          {scope === 'all' && receiver.emailAddress && (
                            <span className="receiver-mcard-meta-item">{receiver.emailAddress}</span>
                          )}
                          <SoftwareCell meta={m} receiver={receiver} />
                          {m?.softwareVersion && <span className="receiver-mcard-meta-item">{m.softwareVersion}</span>}
                          {type && <span className="receiver-mcard-meta-item">{type}</span>}
                          {sw === null && !type && <span className="muted">—</span>}
                          <TrafficBadges types={types} />
                          <UpdateCell meta={m} onOpenDevice={() => openDetail(receiver, 'config')} />
                        </div>

                        <div className="receiver-mcard-stats">
                          <div className="receiver-mcard-stat">
                            <div className="receiver-mcard-stat-value">
                              {receiver.beastMsgRate > 0 ? receiver.beastMsgRate.toFixed(1)
                                : receiver.acarsMsgRate > 0 ? receiver.acarsMsgRate.toFixed(1) : '—'}
                            </div>
                            <div className="receiver-mcard-stat-label">msg/s</div>
                          </div>
                          <div className="receiver-mcard-stat">
                            <div className="receiver-mcard-stat-value">{fmtCount(counts.aircraft) ?? '—'}</div>
                            <div className="receiver-mcard-stat-label">aircraft today</div>
                          </div>
                          <div className="receiver-mcard-stat">
                            <div className="receiver-mcard-stat-value">{fmtCount(counts.msgs) ?? '—'}</div>
                            <div className="receiver-mcard-stat-label">msgs today</div>
                          </div>
                          <div className="receiver-mcard-stat">
                            <div className="receiver-mcard-stat-value receiver-mcard-stat-value-text">
                              {locationLabel(receiver) || formatDate(receiver.lastActiveAt)}
                            </div>
                            <div className="receiver-mcard-stat-label">{locationLabel(receiver) ? 'location' : 'last active'}</div>
                          </div>
                        </div>

                        <div className="receiver-mcard-actions">
                          {quickLinks(receiver, m, 'receiver-mcard-action')}
                        </div>
                        <div className="receiver-mcard-actions">
                          <button className="receiver-mcard-view" onClick={(e) => { e.stopPropagation(); openDetail(receiver); }}>View details</button>
                          {onViewLive && (
                            <button className="receiver-mcard-view" onClick={(e) => { e.stopPropagation(); viewLive(receiver); }}>View Live</button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="receivers-table-container">
                  <table className="receivers-table">
                    <thead>
                      <tr>
                        <th className="sortable" onClick={() => toggleSort('receiverName')}>Name{sortMark('receiverName')}</th>
                        {scope === 'all' && <th className="sortable" onClick={() => toggleSort('emailAddress')}>Owner{sortMark('emailAddress')}</th>}
                        <th className="sortable" onClick={() => toggleSort('status')}>Status{sortMark('status')}</th>
                        <th className="sortable" onClick={() => toggleSort('software')}>Software{sortMark('software')}</th>
                        <th className="sortable" onClick={() => toggleSort('deviceType')}>Type{sortMark('deviceType')}</th>
                        <th className="sortable" onClick={() => toggleSort('version')}>Version{sortMark('version')}</th>
                        <th className="sortable" onClick={() => toggleSort('traffic')}>Traffic{sortMark('traffic')}</th>
                        <th className="sortable" onClick={() => toggleSort('rate')}>Rate{sortMark('rate')}</th>
                        <th className="sortable" onClick={() => toggleSort('location')}>Location{sortMark('location')}</th>
                        <th className="sortable" onClick={() => toggleSort('lastActive')}>Last Active{sortMark('lastActive')}</th>
                        <th className="sortable num-col" onClick={() => toggleSort('aircraftToday')} title="Distinct aircraft seen today (UTC day)">Aircraft Today{sortMark('aircraftToday')}</th>
                        <th className="sortable num-col" onClick={() => toggleSort('messagesToday')} title="Messages received today (UTC day)">Msgs Today{sortMark('messagesToday')}</th>
                        <th className="sortable" onClick={() => toggleSort('backlog')} title="How far behind the receiver's stream is running">Backlog{sortMark('backlog')}</th>
                        <th className="sortable" onClick={() => toggleSort('update')}>Update{sortMark('update')}</th>
                        <th>Quick Links</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayRows.map(receiver => {
                        if (receiver.__group) {
                          return (
                            <tr key={`group-${receiver.email}`} className="owner-group-row">
                              <td colSpan={16}>
                                <span className="owner-group-name">{receiver.email}</span>
                                <span className="owner-group-meta">
                                  {receiver.count} receiver{receiver.count === 1 ? '' : 's'}
                                  {' · '}{receiver.msgs.toLocaleString()} msgs today
                                  {' · '}{receiver.aircraft.toLocaleString()} aircraft today
                                </span>
                              </td>
                            </tr>
                          );
                        }
                        const status = getStatus(receiver);
                        const stats = getReceiverStats(receiver.receiverUuid);
                        const m = meta[receiver.receiverUuid];
                        const counts = todayCounts(stats, m);
                        const loc = locationLabel(receiver);
                        const coords = coordinatesLabel(receiver);
                        return (
                          <tr key={receiver.receiverUuid} className="receiver-row" onClick={() => openDetail(receiver)}>
                            <td className="receiver-name-cell">{receiver.receiverName}</td>
                            {scope === 'all' && <td className="receiver-owner-cell">{receiver.emailAddress || <span className="muted">—</span>}</td>}
                            <td><StatusBadge status={status} /></td>
                            <td><SoftwareCell meta={m} receiver={receiver} /></td>
                            <td>{deviceTypeLabel(m, receiver) || <span className="muted">—</span>}</td>
                            <td>{m?.softwareVersion || <span className="muted">—</span>}</td>
                            <td><TrafficBadges types={trafficTypes(receiver, stats)} /></td>
                            <td className="socket-rate-cell"><RateCell receiver={receiver} /></td>
                            <td title={coords && loc !== coords ? `${coords}${receiver.airportIcao ? ` · ${receiver.airportIcao}` : ''}` : (receiver.airportIcao || undefined)}>
                              {loc || <span className="muted">Not specified</span>}
                              {receiver.airportIcao && <span className="location-airport">{receiver.airportIcao}</span>}
                            </td>
                            <td>{formatDate(receiver.lastActiveAt || receiver.lastConnectedAt)}</td>
                            <td className="num-cell">{fmtCount(counts.aircraft) ?? <span className="muted">—</span>}</td>
                            <td className="num-cell">{fmtCount(counts.msgs) ?? <span className="muted">—</span>}</td>
                            <td><BacklogCell ms={m?.backlogMillis} /></td>
                            <td><UpdateCell meta={m} onOpenDevice={() => openDetail(receiver, 'config')} /></td>
                            <td>{quickLinks(receiver, m)}</td>
                            <td className="receiver-actions-cell">
                              <button className="btn-view" onClick={(e) => { e.stopPropagation(); openDetail(receiver); }}>View</button>
                              {onViewLive && (
                                <button className="btn-view btn-view-live" onClick={(e) => { e.stopPropagation(); viewLive(receiver); }}>View Live</button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Receivers;
